/*
 * (C) 2026 BeyondDemise, All Rights Reserved.
 *
 * Application entry point. Boots into the shell for an authenticated admin,
 * else the login flow, and routes the component events:
 *   ba-login {admin}  → shell (admin) or not-authorized (non-admin)
 *   ba-logout         → authService.logout()
 *   ba-not-authorized → not-authorized screen (mid-session 403)
 *   ba-session-expired→ login (surviving 401 after refresh)
 * No router — event-driven view swapping, like app-frontend's main.ts.
 */

// WebAwesome components
import '@awesome.me/webawesome-pro/dist/components/badge/badge.js';
import '@awesome.me/webawesome-pro/dist/components/button/button.js';
import '@awesome.me/webawesome-pro/dist/components/callout/callout.js';
import '@awesome.me/webawesome-pro/dist/components/card/card.js';
import '@awesome.me/webawesome-pro/dist/components/drawer/drawer.js';
import '@awesome.me/webawesome-pro/dist/components/icon/icon.js';
import '@awesome.me/webawesome-pro/dist/components/input/input.js';
import '@awesome.me/webawesome-pro/dist/components/page/page.js';
import '@awesome.me/webawesome-pro/dist/components/spinner/spinner.js';

// WebAwesome styles
import '@awesome.me/webawesome-pro/dist/styles/webawesome.css';
import '@awesome.me/webawesome-pro/dist/styles/native.css';
import '@awesome.me/webawesome-pro/dist/styles/themes/awesome.css';

import './styles/main.css';

import { authService } from './services/auth.js';
import { BA_LOGIN_EVENT, type BaLoginDetail } from './components/BaLogin.js';
import './components/BaLogin.js';
import { BA_LOGOUT_EVENT } from './components/BaNotAuthorized.js';
import './components/BaNotAuthorized.js';
import './components/BaShell.js';
import {
  BA_NOT_AUTHORIZED_EVENT,
  BA_SESSION_EXPIRED_EVENT,
} from './components/BaCustomerList.js';
import './components/BaCustomerList.js';
import './components/BaCustomerDetail.js';

/** Replace the container's content with a freshly created element. */
const mount = (container: HTMLElement, tag: string): HTMLElement => {
  const element = document.createElement(tag);
  container.replaceChildren(element);
  return element;
};

export const showLogin = (container: HTMLElement): HTMLElement =>
  mount(container, 'ba-login');

export const showShell = (container: HTMLElement): HTMLElement =>
  mount(container, 'ba-shell');

export const showNotAuthorized = (container: HTMLElement): HTMLElement =>
  mount(container, 'ba-not-authorized');

/** Wire the component events once, on the app container. */
export const wireEvents = (container: HTMLElement): void => {
  container.addEventListener(BA_LOGIN_EVENT, event => {
    const detail = (event as CustomEvent<BaLoginDetail>).detail;
    if (detail.admin) {
      showShell(container);
    } else {
      showNotAuthorized(container);
    }
  });
  container.addEventListener(BA_LOGOUT_EVENT, () => authService.logout());
  container.addEventListener(BA_NOT_AUTHORIZED_EVENT, () =>
    showNotAuthorized(container)
  );
  container.addEventListener(BA_SESSION_EXPIRED_EVENT, () =>
    showLogin(container)
  );
};

export const initApp = async (): Promise<void> => {
  const container = document.getElementById('app');
  if (!container) {
    console.error('main.ts: #app container not found');
    return;
  }
  wireEvents(container);
  if ((await authService.isAuthenticated()) && authService.isAdmin()) {
    showShell(container);
  } else {
    showLogin(container);
  }
};

export const startApp = (): void => {
  initApp().catch(error => {
    console.error('Failed to initialize app:', error);
  });
};

declare global {
  interface Window {
    __BA_SKIP_MAIN_AUTOSTART__?: boolean;
  }
}

if (typeof window !== 'undefined' && !window.__BA_SKIP_MAIN_AUTOSTART__) {
  if (document.readyState !== 'loading') {
    startApp();
  } else {
    document.addEventListener('DOMContentLoaded', startApp);
  }
}
